const REPORT_BRANDING_STORAGE_KEY = "reportBranding";

const pickFirstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

const readStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(REPORT_BRANDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = () => reject(new Error("Failed to convert blob to data URL."));
    reader.readAsDataURL(blob);
  });

const getOrganizationCandidate = (source) => {
  if (!source || typeof source !== "object") return null;
  return source.organization || source.org || source;
};

const sanitizeOrganizationSettings = (settings) => {
  if (!settings || typeof settings !== "object") return {};

  return {
    height_of_house: settings.height_of_house,
    must_height: settings.must_height,
    highest_can: settings.highest_can,
    horizontalScanRadius: settings.horizontalScanRadius,
    horizontalScanPoints: settings.horizontalScanPoints,
    circleOneRadius: settings.circleOneRadius,
    circleOnePoints: settings.circleOnePoints,
    circleTwoRadius: settings.circleTwoRadius,
    circleTwoPoints: settings.circleTwoPoints,
  };
};

export const persistReportBranding = (userData) => {
  if (typeof window === "undefined" || !userData || typeof userData !== "object") return;

  const organization = getOrganizationCandidate(userData.organization) || getOrganizationCandidate(userData);
  const branding = {
    organizationName: pickFirstNonEmpty(
      organization?.name,
      userData?.organization_name,
      userData?.org_name
    ),
    organizationImage: pickFirstNonEmpty(organization?.organization_image),
    organizationDarkImage: pickFirstNonEmpty(organization?.organization_dark_image),
    organizationSettings: sanitizeOrganizationSettings(organization?.settings),
  };

  try {
    window.localStorage.setItem(REPORT_BRANDING_STORAGE_KEY, JSON.stringify(branding));
  } catch (_error) {}
};

export const getPreferredOrganizationLogoUrl = (...sources) => {
  const candidates = [...sources, readStorage()];

  for (const source of candidates) {
    const organization = getOrganizationCandidate(source);
    const logoUrl = pickFirstNonEmpty(
      organization?.organization_dark_image,
      organization?.organization_image,
      source?.organizationDarkImage,
      source?.organizationImage
    );
    if (logoUrl) return logoUrl;
  }

  return "";
};

export const getPreferredOrganizationSettings = (...sources) => {
  const candidates = [...sources, readStorage()];

  for (const source of candidates) {
    const organization = getOrganizationCandidate(source);
    const settings = sanitizeOrganizationSettings(
      organization?.settings || source?.organizationSettings
    );
    if (Object.keys(settings).length > 0) return settings;
  }

  return {};
};

export const loadImageAsDataUrl = async (imageUrl) => {
  if (!imageUrl) return "";

  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return "";
    const blob = await response.blob();
    const dataUrl = await blobToDataUrl(blob);
    return typeof dataUrl === "string" ? dataUrl : "";
  } catch (_error) {
    return "";
  }
};

export const loadImageDimensions = (imageSource) =>
  new Promise((resolve) => {
    if (!imageSource || typeof Image === "undefined") {
      resolve(null);
      return;
    }

    try {
      const img = new Image();
      img.onload = () => {
        const width = Number(img.naturalWidth || img.width);
        const height = Number(img.naturalHeight || img.height);
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
          resolve(null);
          return;
        }
        resolve({ width, height });
      };
      img.onerror = () => resolve(null);
      img.src = imageSource;
    } catch (_error) {
      resolve(null);
    }
  });

export const fitImageContain = (
  dimensions,
  x,
  y,
  maxWidth,
  maxHeight,
  options = {}
) => {
  const safeMaxWidth = Math.max(0, Number(maxWidth) || 0);
  const safeMaxHeight = Math.max(0, Number(maxHeight) || 0);
  const alignX = options.alignX === "left" ? "left" : options.alignX === "right" ? "right" : "center";
  const alignY = options.alignY === "top" ? "top" : options.alignY === "bottom" ? "bottom" : "center";

  if (!dimensions?.width || !dimensions?.height || safeMaxWidth <= 0 || safeMaxHeight <= 0) {
    return { x, y, width: safeMaxWidth, height: safeMaxHeight };
  }

  const widthRatio = safeMaxWidth / dimensions.width;
  const heightRatio = safeMaxHeight / dimensions.height;
  const scale = Math.min(widthRatio, heightRatio);
  const width = dimensions.width * scale;
  const height = dimensions.height * scale;
  const offsetX =
    alignX === "left"
      ? 0
      : alignX === "right"
      ? safeMaxWidth - width
      : (safeMaxWidth - width) / 2;
  const offsetY =
    alignY === "top"
      ? 0
      : alignY === "bottom"
      ? safeMaxHeight - height
      : (safeMaxHeight - height) / 2;

  return {
    x: x + offsetX,
    y: y + offsetY,
    width,
    height,
  };
};

export const trimImageWhitespace = (imageSource) =>
  new Promise((resolve) => {
    if (!imageSource || typeof Image === "undefined" || typeof document === "undefined") {
      resolve(imageSource || "");
      return;
    }

    try {
      const img = new Image();
      img.onload = () => {
        try {
          const width = Number(img.naturalWidth || img.width) || 0;
          const height = Number(img.naturalHeight || img.height) || 0;
          if (width <= 0 || height <= 0) {
            resolve(imageSource);
            return;
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) {
            resolve(imageSource);
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);
          const { data } = ctx.getImageData(0, 0, width, height);

          let minX = width;
          let minY = height;
          let maxX = -1;
          let maxY = -1;

          for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
              const idx = (y * width + x) * 4;
              const r = data[idx];
              const g = data[idx + 1];
              const b = data[idx + 2];
              const a = data[idx + 3];
              const isVisible = a > 10;
              const isNearWhite = r > 245 && g > 245 && b > 245;
              if (!isVisible || isNearWhite) continue;

              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
            }
          }

          if (maxX < minX || maxY < minY) {
            resolve(imageSource);
            return;
          }

          const cropWidth = maxX - minX + 1;
          const cropHeight = maxY - minY + 1;
          if (cropWidth === width && cropHeight === height) {
            resolve(imageSource);
            return;
          }

          const trimmedCanvas = document.createElement("canvas");
          trimmedCanvas.width = cropWidth;
          trimmedCanvas.height = cropHeight;
          const trimmedCtx = trimmedCanvas.getContext("2d");
          if (!trimmedCtx) {
            resolve(imageSource);
            return;
          }

          trimmedCtx.drawImage(
            canvas,
            minX,
            minY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight
          );

          resolve(trimmedCanvas.toDataURL("image/png"));
        } catch (_error) {
          resolve(imageSource);
        }
      };
      img.onerror = () => resolve(imageSource);
      img.src = imageSource;
    } catch (_error) {
      resolve(imageSource);
    }
  });
